import { CheckCircle, CopyAllOutlined, Percent } from "@mui/icons-material";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import DiamondIcon from "@mui/icons-material/Diamond";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FiberNewIcon from "@mui/icons-material/FiberNew";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import RepeatIcon from "@mui/icons-material/Repeat";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import {
  Alert,
  Avatar,
  Chip,
  ChipProps,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  InputAdornment,
  Typography,
  alpha,
} from "@mui/material";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import { Permissions } from "app/constants";
import { useAppSelector } from "app/hooks";
import {
  Autocomplete,
  Button,
  ButtonGroup,
  Checkbox,
  CreatedUpdatedBy,
  DateTimePicker,
  Field,
  FieldsContainer,
  Form,
  FormSectionTitle,
  PageHeader,
  Spacer,
  TextField,
  Toolbar,
  globalMessageHandler,
} from "common";
import { ParamsContext } from "common/components/Form/ParamsContext";
import CounterButton from "common/components/Inputs/CounterButton/CounterButton";
import Price from "common/components/Inputs/Price/Price";
import AttachBookingButton from "components/AttachBookingButton/AttachBookingButton";
import { BOOKING_DOCUMENT_TYPES } from "features/booking-documents/bookingDocumentApi";
import BookingDocumentListModal from "features/booking-documents/BookingDocumentListModal";
import {
  BOOKING_PAY_LOCATIONS,
  BOOKING_STATUSES,
  PAYMENT_STATUSES,
} from "features/bookings/bookingConstants";
import { useExchangeRate } from "features/exchange-rates/ExchangeRateProvider";
import HistoryListDialog from "features/histories/HistoryListDialog";
import { selectProfile } from "features/profile/profileSlice";
import useAccess from "features/profile/useAccess";
import UpgradeButton from "features/upgrades/UpgradeButton/UpgradeButton";
import { getPrice } from "helpers/priceHelper";
import I18n from "i18n";
import { size } from "lodash";
import moment from "moment";
import { createContext, useState } from "react";
import BillingDataInput from "./BillingDataInput";
import { resendSelfSeatingInvitation, setRefundAt } from "./bookingApi";
import BookingOptionSelector from "./BookingOptionSelector";
import BookingReadyActions from "./BookingReadyActions";
import MoveBookingListModal from "./MoveBookings/MoveBookingListModal";
import PaymentManagementDialog from "./PaymentManagementDialog";
import RefundBookingListModal from "./RefundBookings/RefundBookingListModal";
import useBookingForm, {
  UseBookingFormProps,
  UseBookingFormReturnType,
} from "./useBookingForm";
export const BookingContext = createContext<
  UseBookingFormReturnType | undefined
>(undefined);
export interface BookingFormProps extends UseBookingFormProps {}

export default function BookingForm({
  id,
  afterSave,
  onClose,
  defaultValues,
  listItem,
  isPartnerView,
  createPrivateEventData,
}: BookingFormProps) {
  const { hasPermissions, canEditItem } = useAccess();
  const { exchangeRate, currency, setCurrency, getEur, getHUF, getVal } =
    useExchangeRate();
  const form = useBookingForm({
    id,
    afterSave,
    defaultValues,
    listItem,
    isPartnerView,
    createPrivateEventData,
  });
  console.log("Booking form render", {
    defaultValues,
    errors: form.errors,
    item: form.item,
    isUpdate: form.isUpdate,
  });
  const {
    params,
    item,
    options,
    errors,
    loading,
    isDirty,
    isCreate,
    isUpdate,
    getIsDirty,
    getRequiredFields,
    onChange,
    save,
    saving,
    activateBooking,
    cancelBooking,
    setAttributes,
    statusChanging,
    capitalizeWords,
    setCapitalizeWords,
    event,
    priceMode,
    setPriceMode,
    load,
    selectedPartnerGroup,
    copyBookingDataToBilling,
  } = form;
  const profile = useAppSelector(selectProfile);
  const [bookingsToMove, setBookingsToMove] = useState<{
    bookingIds: number[];
    programIds: number[];
  }>();
  const [bookingsToRefund, setBookingsToRefund] = useState<{
    bookingIds: number[];
  }>();
  const [refundUnit, setRefundUnit] = useState<"amount" | "percent">("percent");

  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [documentModalOpen, setDocumentModalOpen] = useState(false);
  const [resendingSelfSeating, setResendingSelfSeating] = useState(false);

  const openPaymentDialog = async () => {
    if (!item?.id) {
      globalMessageHandler.snack({
        severity: "error",
        message: "A foglalás mentése után kezelhető a fizetés.",
      });
      return;
    }
    if (isDirty) {
      globalMessageHandler.confirm(
        {
          message: I18n.t("Booking.saveBeforePayment"),
          buttonsProps: {
            yes: { title: I18n.t("Booking.saveBeforePaymentConfirm") },
            cancel: { title: I18n.t("App.cancel") },
            extra: { title: I18n.t("Booking.saveBeforePaymentDiscard") },
          },
        },
        async () => {
          await save();
          setPaymentDialogOpen(true);
        },
        undefined,
        async () => {
          form.setItem(form.originalValues);
          setPaymentDialogOpen(true);
        },
      );
      return;
    }
    setPaymentDialogOpen(true);
  };
  const start_time = item?.planned ? item?.planned_datetime : event?.start_time;
  const title = isCreate
    ? `${event?.program?.name} ${moment(start_time).format("L LT")}`
    : `${event?.program?.name} ${moment(start_time).format("L LT")}`;
  const createdByPartnerSuffix = item?.created_by_partner?.name
    ? ` [${item.created_by_partner.name}]`
    : "";
  const subtitle = isCreate
    ? `${event?.boat?.name}${createdByPartnerSuffix}`
    : `${item?.booking_number} ${event?.boat?.name}${createdByPartnerSuffix}`;

  const disabled =
    /*item.status === BOOKING_STATUSES.CANCELED ||*/ !canEditItem(
      "booking",
      item,
    );

  const hasVisibleOptions =
    !!event?.program?.has_options &&
    (options?.booking_options as any[])?.length > 0;

  const prices = getPrice(item);
  const selectedVariantId =
    item?.program_variant_id || item?.program_variant?.id;
  const bookingVariants = [
    ...((item as any)?.variants || []),
    ...(item?.event?.variants || []),
    ...(event?.variants || []),
    ...(((options as any)?.program_variant_id as any[]) || []),
  ];
  const selectedBookingVariant = bookingVariants?.find((variant: any) => {
    const variantKeys = [variant?.variant_id, variant?.id, variant?.value];
    return variantKeys.some(key => String(key) === String(selectedVariantId));
  });
  const variantAdultPrice = selectedBookingVariant?.adult_price;
  const variantKidPrice = selectedBookingVariant?.kid_price;
  const variantCurrency = item?.currency || "EUR";
  const variantDefaultPriceLabel =
    selectedBookingVariant && (variantAdultPrice || variantAdultPrice === 0)
      ? `Alapár: Felnőtt: ${variantAdultPrice} ${variantCurrency}${
          Number(variantKidPrice) > 0
            ? `, Gyerek: ${variantKidPrice} ${variantCurrency}`
            : ""
        }`
      : undefined;

  return (
    <ParamsContext.Provider
      value={{
        booking_id: id,
        event_id: item.event_id,
        program_variant_id: item.program_variant_id,
        partner_id: item.partner_id,
      }}
    >
      <BookingContext.Provider value={form}>
        <PageHeader
          avatar={
            <Avatar
              sx={{
                backgroundColor: event?.program?.color,
                color: theme =>
                  event?.program?.color
                    ? theme.palette.getContrastText(event?.program?.color)
                    : undefined,
              }}
            >
              {event?.program?.code}
            </Avatar>
          }
          title={title}
          subtitle={isPartnerView ? undefined : subtitle}
          onClose={onClose}
          //loading={loading}
        >
          {isCreate && <FiberNewIcon color="info" fontSize="large" />}
        </PageHeader>
        {!!item.planned && (
          <Alert
            severity="warning"
            sx={{ borderRadius: 0 }}
            action={
              isUpdate ? (
                <AttachBookingButton
                  buttonType="button"
                  title="Csatolás"
                  variant="text"
                  color="info"
                  size="small"
                  booking={item}
                  onSelect={event => {
                    //Variáns kiválasztása vagy törlése
                    let program_variant_id: number | undefined =
                      item.program_variant_id;
                    if (event?.variants?.length === 1) {
                      program_variant_id = event?.variants[0].variant_id;
                    }
                    if (event?.variants && event?.variants?.length > 1) {
                      const variant_found = event?.variants.find(
                        e => e.variant_id === program_variant_id,
                      );
                      if (!variant_found) {
                        program_variant_id = undefined;
                      }
                    }

                    setAttributes({
                      program_variant_id,
                      event_id: event?.id,
                      event: event as any,
                      planned_datetime: null as any,
                      planned_event: null as any,
                      //event: event as any,
                      planned_event_id: null as any,
                      planned: false,
                      //status: EVENT_STATUSE.ACTIVE,
                    });
                  }}
                />
              ) : undefined
            }
          >
            {I18n.t("Booking.plannedNotification", {
              date: moment(event?.start_time).format("L LT"),
            })}
          </Alert>
        )}
        {item.status === BOOKING_STATUSES.CANCELED && (
          <Alert severity="error" square>
            {I18n.t("Booking.cancelledBooking")}
          </Alert>
        )}
        <DialogContent>
          <Form
            loading={loading}
            item={item}
            options={options}
            errors={errors}
            onChange={onChange}
            translationCategory="Booking.attributes"
            translationPlaceholdersCategory="Booking.placeholders"
            translationHelpersCategory="Booking.helpers"
            isDirty={getIsDirty}
            requiredFields={getRequiredFields()}
            disabled={disabled}
          >
            <Grid container spacing={3}>
              <Grid item sm={12} md={hasVisibleOptions ? 6 : 12}>
                <FieldsContainer>
                  {size((options as any)?.program_variant_id) > 1 && (
                    <Field fullWidth>
                      <ButtonGroup
                        id="program_variant_id"
                        //disabled={!!params?.program_variant_id}
                      />
                    </Field>
                  )}
                  {!!item.planned && (
                    <Field fullWidth>
                      <DateTimePicker id="planned_datetime" />
                    </Field>
                  )}
                  {(!params?.partner_id || isUpdate) && !isPartnerView && (
                    <Field fullWidth>
                      {!!item.custom_partner ? (
                        <TextField
                          useDelayedKeystrokes={false}
                          keepCursorPosition
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              pl: 1,
                            },
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Button
                                  variant="text"
                                  size="small"
                                  title={
                                    item.custom_partner ? "Egyedi" : "Meglévő"
                                  }
                                  onClick={() => {
                                    form.setAttributes({
                                      custom_partner: !item.custom_partner,
                                    });
                                  }}
                                />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  color={
                                    capitalizeWords?.partner_name
                                      ? "info"
                                      : "default"
                                  }
                                  size="small"
                                  title={
                                    capitalizeWords?.partner_name
                                      ? "Nagybetűs szavak bekapcsolva"
                                      : "Nagybetűs szavak kikapcsolva"
                                  }
                                  onClick={() => {
                                    setCapitalizeWords({
                                      ...capitalizeWords,
                                      partner_name:
                                        !capitalizeWords?.partner_name,
                                    });
                                  }}
                                >
                                  <TextFieldsIcon />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          id="partner_name"
                        />
                      ) : (
                        <Autocomplete
                          options={
                            options?.recent_partner_id && options?.partner_id
                              ? [
                                  ...options.recent_partner_id.map(
                                    (p: any) => ({
                                      ...p,
                                      group: "Legutóbbi partnerek",
                                    }),
                                  ),
                                  ...options.partner_id
                                    .filter(
                                      (p: any) =>
                                        !options.recent_partner_id.find(
                                          (rp: any) => rp.value === p.value,
                                        ),
                                    )
                                    .map((p: any) => ({
                                      ...p,
                                      group: "Összes partner",
                                    })),
                                ]
                              : options?.partner_id
                          }
                          groupBy={(option: any) => option.group}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Button
                                  variant="text"
                                  size="small"
                                  title={
                                    item.custom_partner ? "Egyedi" : "Meglévő"
                                  }
                                  onClick={() => {
                                    form.setAttributes({
                                      custom_partner: !item.custom_partner,
                                    });
                                  }}
                                />
                              </InputAdornment>
                            ),
                          }}
                          id="partner_id"
                        />
                      )}
                    </Field>
                  )}
                  {isPartnerView && (
                    <Field fullWidth>
                      <Autocomplete id="partner_id" disabled />
                    </Field>
                  )}

                  {!isPartnerView && (
                    <Field fullWidth>
                      <Autocomplete id="sales_person_id" />
                    </Field>
                  )}
                  {/*!params?.event_id &&
                    !item.planned &&
                    item.status === BOOKING_STATUSES.ACTIVE && (
                      <Field>
                        <Autocomplete id="event_id" />
                      </Field>
                    )*/}
                  <Field fullWidth>
                    <TextField
                      useDelayedKeystrokes={false}
                      keepCursorPosition
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          pl: 1,
                        },
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              color={capitalizeWords?.name ? "info" : "default"}
                              size="small"
                              title={
                                capitalizeWords?.name
                                  ? "Nagybetűs szavak bekapcsolva"
                                  : "Nagybetűs szavak kikapcsolva"
                              }
                              onClick={() => {
                                setCapitalizeWords({
                                  ...capitalizeWords,
                                  name: !capitalizeWords?.name,
                                });
                              }}
                            >
                              <TextFieldsIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      id="name"
                    />
                  </Field>
                  {!isPartnerView && (
                    <Field xs={12}>
                      <TextField id="external_booking_number" />
                    </Field>
                  )}
                  {!isPartnerView && (
                    <Field half>
                      <TextField id="voucher" />
                    </Field>
                  )}
                  {(!isPartnerView || !profile.partner?.monthly_billing) && (
                    <Field half>
                      <Autocomplete id="pay_location" />
                    </Field>
                  )}
                  {/*<Field third>
                    <TextField id="table_numbers" />
                  </Field>*/}
                  <Field half>
                    <TextField id="email" />
                  </Field>
                  <Field half>
                    <TextField id="phone" />
                  </Field>
                  {!isPartnerView &&
                    isUpdate &&
                    event?.program?.self_seating && (
                      <Field xs={12}>
                        <TextField id="self_seating_pin" disabled />
                        {hasPermissions([
                          Permissions.BookingResendSelfSeating,
                        ]) && (
                          <Button
                            sx={{ mt: 1 }}
                            title={I18n.t(
                              "Booking.resendSelfSeatingInvitation",
                            )}
                            loading={resendingSelfSeating}
                            onClick={async () => {
                              setResendingSelfSeating(true);
                              const { success } =
                                await resendSelfSeatingInvitation(item.id);
                              setResendingSelfSeating(false);
                              if (success) {
                                globalMessageHandler.snack({
                                  severity: "success",
                                  message: I18n.t(
                                    "Booking.selfSeatingInvitationQueued",
                                  ),
                                });
                              }
                            }}
                          />
                        )}
                      </Field>
                    )}
                  <Grid container />
                  <Field xs={12} md={3}>
                    <CounterButton id="number_of_adults" fullWidth />
                  </Field>
                  {event?.program?.kid_ticket_available && (
                    <Field xs={12} md={3}>
                      <CounterButton id="number_of_kids" fullWidth />
                    </Field>
                  )}
                  <Field xs={12} md={3}>
                    <CounterButton id="number_of_babies" fullWidth />
                  </Field>
                  {!isPartnerView && (
                    <Field xs={12} md={3}>
                      <CounterButton id="number_of_guides" fullWidth />
                    </Field>
                  )}
                  <Grid container />
                  <Field xs={12} md={3}>
                    <Price
                      mode={priceMode}
                      count={item.number_of_adults}
                      id="adult_price"
                      disabled={
                        !hasPermissions("booking.editprice") || disabled
                      }
                      helperText={
                        priceMode === "total"
                          ? "Össz árat kell megadni!"
                          : undefined
                      }
                    />
                  </Field>
                  {event?.program?.kid_ticket_available && (
                    <Field xs={12} md={3}>
                      <Price
                        mode={priceMode}
                        id="kid_price"
                        fullWidth
                        disabled={
                          !hasPermissions("booking.editprice") || disabled
                        }
                        helperText={
                          priceMode === "total"
                            ? "Össz árat kell megadni!"
                            : undefined
                        }
                      />
                    </Field>
                  )}
                  {!isPartnerView && (
                    <Field xs={12} md={3}>
                      <Price
                        id="admin_fee"
                        disabled={
                          !hasPermissions("booking.editprice") || disabled
                        }
                      />
                    </Field>
                  )}
                  {!!item.upgrade && (
                    <Field xs={12} md={3}>
                      <Price
                        id="upgrade_price"
                        fullWidth
                        disabled={
                          !hasPermissions("booking.editprice") || disabled
                        }
                      />
                    </Field>
                  )}
                  <Field xs={12} md={3}>
                    <Price
                      id="rebook_price"
                      fullWidth
                      disabled={
                        !hasPermissions("booking.editprice") || disabled
                      }
                    />
                  </Field>
                  <Field xs={12} md={3}>
                    <Autocomplete id="currency" />
                  </Field>
                  {/*<Field third>
                    <DateTimePicker id="check_in_at" />
                  </Field>*/}
                  <Field fullWidth>
                    <TextField
                      id="description"
                      multiline
                      sx={{ "& textarea": { paddingTop: 0, paddingBottom: 0 } }}
                    />
                  </Field>
                  {!isPartnerView && (
                    <>
                      {!!item.refund_eligible && (
                        <>
                          <FormSectionTitle
                            title="Refund"
                            titleProps={{
                              variant: "subtitle1",
                              fontWeight: 500,
                            }}
                          />
                          <Field fullWidth>
                            <Toolbar
                              sx={{ gap: 2 }}
                              /*sx={{
                        backgroundColor: theme =>
                          alpha(theme.palette.info.main, 0.1),
                        p: 1,
                        borderRadius: theme => `${theme.shape.borderRadius}px`,
                        position: "relative",
                      }}*/
                            >
                              <ButtonGroup
                                sx={{
                                  flex: 0,
                                  minWidth: 220,
                                  maxWidth: 220,
                                  "& .MuiButton-root.MuiButtonGroup-grouped":
                                    {},
                                }}
                                //label="Refund"
                                //formControlProps={{ sx: { minWidth: 200 } }}
                                //row
                                size="small"
                                disabled={
                                  !hasPermissions(
                                    Permissions.BookingRefundEligible,
                                  )
                                }
                                value={refundUnit}
                                options={[
                                  { value: "percent", label: "Százalék" },
                                  { value: "amount", label: "Összeg" },
                                ]}
                                onChange={(evt: any) => {
                                  setRefundUnit(evt.target.value);
                                }}
                              />
                              {refundUnit === "amount" && (
                                <Price
                                  mode={"normal"}
                                  id="refund_amount"
                                  disabled={
                                    !hasPermissions(
                                      Permissions.BookingRefundEligible,
                                    )
                                  }
                                />
                              )}
                              {refundUnit === "percent" && (
                                <TextField
                                  mask={Number}
                                  label={I18n.t(
                                    "Booking.attributes.refund_percent",
                                  )}
                                  numberOptions={{
                                    scale: 5,
                                    normalizeZeros: true,
                                    min: 0,
                                    max: 100,
                                  }}
                                  unmask
                                  disabled={
                                    !hasPermissions(
                                      Permissions.BookingRefundEligible,
                                    )
                                  }
                                  value={
                                    item.refund_amount ||
                                    item.refund_amount === 0
                                      ? Math.round(
                                          ((item.refund_amount || 0) /
                                            getPrice(item).sum) *
                                            10000,
                                        ) / 100
                                      : null
                                  }
                                  onChange={(evt: any) => {
                                    console.log({ evt });
                                    setAttributes({
                                      refund_amount:
                                        (evt.target?.value / 100) *
                                        getPrice(item).sum,
                                    });
                                  }}
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <Percent />
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              )}
                            </Toolbar>
                          </Field>
                          <Field fullWidth>
                            <TextField
                              id="refund_reason"
                              disabled={
                                !hasPermissions(
                                  Permissions.BookingRefundEligible,
                                )
                              }
                            />
                          </Field>
                        </>
                      )}
                      <Field fullWidth>
                        <Checkbox id="is_group" />
                        <Checkbox
                          id="should_notify"
                          disabled={!canEditItem("booking", item)}
                        />
                        <Checkbox
                          id="refund_eligible"
                          disabled={
                            !hasPermissions(Permissions.BookingRefundEligible)
                          }
                        />

                        {!!item.refund_eligible && (
                          <Button
                            size="small"
                            variant={item.refund_at ? "contained" : "outlined"}
                            color={item.refund_at ? "success" : "primary"}
                            title={
                              item.refund_at
                                ? "Refund visszavonása"
                                : "Refund elintézve"
                            }
                            disabled={isDirty}
                            sx={{ mt: 0.5, mr: 2 }}
                            onClick={async () => {
                              const isHandled = !!item.refund_at;
                              const { success, data } = await setRefundAt(
                                item.id,
                                isHandled ? false : true,
                              );
                              if (success) {
                                setAttributes(
                                  {
                                    refund_at: isHandled
                                      ? null
                                      : data?.refund_at,
                                  },
                                  true,
                                );
                                afterSave &&
                                  /*afterSave(
                                    {
                                      ...item,
                                      refund_at: isHandled
                                        ? null
                                        : data?.refund_at,
                                    },
                                    false,
                                  );*/
                                  globalMessageHandler.snack({
                                    message: isHandled
                                      ? "Refund visszavonva"
                                      : "Refund elintézve jelölve",
                                    severity: "success",
                                  });
                              }
                            }}
                          />
                        )}

                        <Button
                          size="small"
                          variant={
                            !!item.notified_at ? "contained" : "outlined"
                          }
                          title="Ügyfél értesítve"
                          sx={{ mt: 0.5 }}
                          onClick={() => {
                            const timestamp = moment().format("L LT");
                            const newLine = `[${timestamp}] ${profile.first_name} ${profile.last_name}: Ügyfél értesítve.`;
                            const current = item.notify_description
                              ? `${item.notify_description}\n`
                              : "";
                            // Csak akkor adja hozzá, ha még nincs benne pontosan ez a sor
                            if (!!item.notified_at) {
                              form.setAttributes({
                                notified_at: null,
                              });
                            } else if (!current.includes(newLine)) {
                              form.setAttributes({
                                notify_description: current + newLine,
                                notified_at: moment().toISOString(),
                              });
                            } else {
                              form.setAttributes({
                                notified_at: moment().toISOString(),
                              });
                            }
                          }}
                        />
                      </Field>
                      <Field fullWidth>
                        <TextField
                          id="notify_description"
                          multiline
                          sx={{
                            "& textarea": { paddingTop: 0, paddingBottom: 0 },
                          }}
                        />
                      </Field>

                      {hasPermissions([Permissions.BookingViewOthers]) && (
                        <Field fullWidth>
                          <Accordion
                            disableGutters
                            sx={{ flex: 1 }}
                            elevation={0}
                            variant="outlined"
                          >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Typography variant="subtitle1" color="info.main">
                                Egyéb
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <FieldsContainer>
                                <Field>
                                  <TextField id="lang" />
                                </Field>
                                <Field>
                                  <Autocomplete
                                    id="source_id"
                                    options={options?.partner_id}
                                  />
                                </Field>
                                <Field>
                                  <Autocomplete
                                    id="created_by_partner_id"
                                    options={options?.partner_id}
                                  />
                                </Field>
                                {variantDefaultPriceLabel && (
                                  <Field fullWidth>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      {variantDefaultPriceLabel}
                                    </Typography>
                                  </Field>
                                )}
                                <Field fullWidth>
                                  <TextField
                                    id="payment_options"
                                    multiline
                                    disabled
                                    value={JSON.stringify(
                                      item.payment_options,
                                      null,
                                      2,
                                    )}
                                  />
                                </Field>
                              </FieldsContainer>
                            </AccordionDetails>
                          </Accordion>
                        </Field>
                      )}
                    </>
                  )}
                  <Field fullWidth>
                    <Checkbox
                      id="customer_requests_invoice"
                      label="Ügyfél számlát kér"
                      /*disabled={
                        isPartnerView && !profile.partner?.monthly_billing
                      }*/
                    />
                  </Field>
                  {item.customer_requests_invoice && (
                    <>
                      <FormSectionTitle
                        title="Számlázási adatok"
                        titleProps={{
                          variant: "subtitle1",
                          fontWeight: 500,
                        }}
                      >
                        <IconButton
                          title="Számlázási adatok másolása a foglalásból"
                          onClick={copyBookingDataToBilling}
                        >
                          <CopyAllOutlined />
                        </IconButton>
                      </FormSectionTitle>
                      <Field fullWidth>
                        <BillingDataInput id="billing_data" />
                      </Field>
                    </>
                  )}
                </FieldsContainer>
              </Grid>
              {hasVisibleOptions && (
                <Grid item sm={12} md={6}>
                  <FieldsContainer>
                    {hasVisibleOptions && (
                      <Field fullWidth>
                        <BookingOptionSelector
                          booking={item}
                          id="booking_options"
                        />
                      </Field>
                    )}
                  </FieldsContainer>
                </Grid>
              )}
            </Grid>
          </Form>
        </DialogContent>

        <Toolbar
          sx={{
            pl: 1,
            pr: 2,
            gap: 2,
            borderTop: theme => `solid 1px ${theme.palette.divider}`,
            borderBottom: theme => `solid 1px ${theme.palette.divider}`,
            backgroundColor: theme =>
              item.paid
                ? alpha(theme.palette.success.main, 0.1)
                : alpha(theme.palette.warning.main, 0.1),
          }}
        >
          {!isPartnerView && (
            <Toolbar>
              <Checkbox
                id="paid"
                value={item.paid}
                onChange={(evt: any) =>
                  setAttributes({ paid: evt.target.value })
                }
                label={I18n.t("Booking.attributes.paid")}
              />
              {!item.paid && <MoneyOffIcon color="error" fontSize="small" />}
            </Toolbar>
          )}
          {item.payment_status && (
            <Toolbar>
              {(() => {
                const colorMap: Record<PAYMENT_STATUSES, ChipProps["color"]> = {
                  [PAYMENT_STATUSES.CREATED]: "default",
                  [PAYMENT_STATUSES.PENDING]: "warning",
                  [PAYMENT_STATUSES.PAID]: "success",
                  [PAYMENT_STATUSES.FAILED]: "error",
                  [PAYMENT_STATUSES.EXPIRED]: "default",
                  [PAYMENT_STATUSES.CANCELLED]: "error",
                };
                return (
                  <Chip
                    size="small"
                    label={I18n.t(
                      `Booking.options.payment_status.${item.payment_status}`,
                    )}
                    color={colorMap[item.payment_status as PAYMENT_STATUSES]}
                  />
                );
              })()}
            </Toolbar>
          )}
          {!!item.upgrade && !isPartnerView && (
            <Toolbar
              sx={{
                position: "relative",
                pl: 1,
                pr: 1,
                borderLeft: theme => `solid 1px ${theme.palette.divider}`,
                borderRight: theme => `solid 1px ${theme.palette.divider}`,
                backgroundColor: theme =>
                  item.upgrade_paid
                    ? alpha(theme.palette.success.main, 0.1)
                    : alpha(theme.palette.warning.main, 0.1),
              }}
            >
              <DiamondIcon />
              <Checkbox
                id="upgrade_paid"
                value={item.upgrade_paid}
                onChange={(evt: any) =>
                  setAttributes({ upgrade_paid: evt.target.value })
                }
                label={I18n.t("Booking.attributes.upgrade_paid")}
              />
              {!item.upgrade_paid && (
                <MoneyOffIcon color="error" fontSize="small" />
              )}
            </Toolbar>
          )}
          {!!item.rebook_price && !isPartnerView && (
            <Toolbar
              sx={{
                position: "relative",
                pl: 1,
                pr: 1,
                borderLeft: theme => `solid 1px ${theme.palette.divider}`,
                borderRight: theme => `solid 1px ${theme.palette.divider}`,
                backgroundColor: theme =>
                  item.rebook_paid
                    ? alpha(theme.palette.success.main, 0.1)
                    : alpha(theme.palette.warning.main, 0.1),
              }}
            >
              <RepeatIcon />
              <Checkbox
                id="rebook_paid"
                value={item.rebook_paid}
                onChange={(evt: any) =>
                  setAttributes({ rebook_paid: evt.target.value })
                }
                label={I18n.t("Booking.attributes.rebook_paid")}
              />
              {!item.rebook_paid && (
                <MoneyOffIcon color="error" fontSize="small" />
              )}
            </Toolbar>
          )}
          <Spacer right />
          <Typography variant="h6">
            {(item.number_of_adults || 0) +
              (item.number_of_kids || 0) +
              (item.number_of_guides || 0) +
              (item.number_of_babies || 0)}{" "}
            Fő
          </Typography>
          {!!item.number_of_babies && (
            <Typography variant="h6" color="success.main">
              {item.number_of_babies || 0} Baba
            </Typography>
          )}
          {!!item.number_of_guides && (
            <Typography variant="h6" color="success.main">
              {item.number_of_guides || 0} Kísérő
            </Typography>
          )}
          <Typography variant="h6">
            {currency === "HUF"
              ? I18n.numberToCurrency(getVal(prices.sum), {
                  precision: 2,
                  unit: "Ft",
                  format: "%n %u",
                  separator: ",",
                  delimiter: " ",
                  stripInsignificantZeros: true,
                })
              : null}
            {currency === "EUR"
              ? I18n.numberToCurrency(prices.sum, {
                  precision: 2,
                  unit: "€",
                  format: "%u %n",
                  separator: ",",
                  delimiter: " ",
                  stripInsignificantZeros: true,
                })
              : null}
          </Typography>
          {prices.remaining > 0 && !isPartnerView && (
            <Toolbar
              sx={{
                pl: 1,
                pr: 1,
                alignSelf: "stretch",
                position: "relative",
                borderLeft: theme => `solid 1px ${theme.palette.divider}`,
                borderRight: theme => `solid 1px ${theme.palette.divider}`,
                backgroundColor: theme => alpha(theme.palette.error.main, 0.2),
              }}
            >
              <Typography
                variant="caption"
                sx={{ position: "absolute", bottom: -2, left: 2, opacity: 0.6 }}
              >
                Fizetendő
              </Typography>
              <Typography variant="h6">
                {I18n.numberToCurrency(prices.remaining, {
                  precision: 2,
                  unit: "€",
                  format: "%u %n",
                  separator: ",",
                  delimiter: " ",
                  stripInsignificantZeros: true,
                })}
              </Typography>
            </Toolbar>
          )}
        </Toolbar>
        {isUpdate &&
          !isPartnerView &&
          item.event?.program?.ready_tracking &&
          hasPermissions([Permissions.BookingReadyCheck]) && (
            <DialogActions
              sx={{
                gap: 1,
                borderBottom: theme => `solid 1px ${theme.palette.divider}`,
                pb: 2,
                mt: 1,
              }}
            >
              <BookingReadyActions
                booking={item}
                variant="buttons"
                size="small"
                onSuccess={updatedData => {
                  setAttributes(updatedData, true);
                  //afterSave && afterSave({ ...item, ...updatedData }, false);
                }}
              />
            </DialogActions>
          )}
        <DialogActions sx={{ gap: 1 }}>
          {isUpdate && item.status === BOOKING_STATUSES.ACTIVE && (
            <Button
              variant="outlined"
              title="Foglalás lemondása"
              disabled={isDirty}
              onClick={() => cancelBooking()}
              loading={statusChanging}
              color="error"
            />
          )}
          {isUpdate && !isPartnerView && (
            <>
              {item.status === BOOKING_STATUSES.CANCELED && (
                <Button
                  variant="outlined"
                  title="Újra aktiválás"
                  disabled={isDirty}
                  onClick={() => activateBooking()}
                  loading={statusChanging}
                  //color="error"
                />
              )}
              {!item.planned && (
                <Button
                  variant="outlined"
                  title="Áthelyezés"
                  /*booking={item}
                  onSelect={event => {
                    setAttributes({
                      event_id: event?.id,
                      event: event as any,
                      planned_datetime: null as any,
                      planned_event: null as any,
                      //event: event as any,
                      planned_event_id: null as any,
                      planned: false,
                      //status: EVENT_STATUSE.ACTIVE,
                    });
                  }}*/
                  onClick={() => {
                    if (item?.program_variant?.program_id) {
                      setBookingsToMove({
                        bookingIds: [item?.id],
                        programIds: [item?.program_variant?.program_id],
                      });
                    }
                  }}
                />
              )}
              <UpgradeButton
                booking={item}
                buttonType="button"
                key="upgrade"
                variant="outlined"
                startIcon={<DiamondIcon />}
                //size="small"
                afterUpgrade={item => {
                  afterSave && afterSave(item, false);
                  //onClose && onClose();
                }}
              >
                Upgrade
              </UpgradeButton>
              <Button
                variant="outlined"
                title={I18n.t("BookingDocument.title")}
                onClick={() => setDocumentModalOpen(true)}
              />
            </>
          )}

          {isUpdate &&
            ((isPartnerView &&
              item?.pay_location === BOOKING_PAY_LOCATIONS.HOTEL_CARD) ||
              !isPartnerView) && (
              <Button
                //variant="outlined"
                title={isPartnerView ? "Kártyás fizetés" : "Fizetés kezelése"}
                disabled={
                  isPartnerView && item.paid
                    ? true
                    : item.status === BOOKING_STATUSES.CANCELED
                }
                onClick={openPaymentDialog}
                startIcon={<CreditCardIcon />}
                endIcon={
                  item.paid ? <CheckCircle color="success" /> : undefined
                }
                color="info"
                sx={
                  isPartnerView
                    ? {
                        justifySelf: "stretch",
                        width: "calc(80% - 100px)!important",
                      }
                    : undefined
                }
              />
            )}
          {size(bookingsToMove?.bookingIds) > 0 && !isPartnerView && (
            <MoveBookingListModal
              bookingIds={bookingsToMove?.bookingIds}
              programId={bookingsToMove?.programIds[0]}
              open={size(bookingsToMove?.bookingIds) > 0}
              onClose={() => setBookingsToMove(undefined)}
              afterMove={() => {
                setBookingsToMove(undefined);
                //form.load();
                afterSave && afterSave({}, false);
                /*if (gridRef.current) {
              //Refresh grid and clear selection
              gridRef.current.loadDeb({ selectedIds: [] });
            }*/
              }}
            />
          )}
          {size(bookingsToRefund?.bookingIds) > 0 && !isPartnerView && (
            <RefundBookingListModal
              bookingIds={bookingsToRefund?.bookingIds}
              open={size(bookingsToRefund?.bookingIds) > 0}
              onClose={() => setBookingsToRefund(undefined)}
              refund={((item.refund_amount || 0) / getPrice(item).sum) * 100}
              afterRefund={() => {
                setBookingsToRefund(undefined);
                form.load();
                //afterSave && afterSave({}, false);
                /*if (gridRef.current) {
              //Refresh grid and clear selection
              gridRef.current.loadDeb({ selectedIds: [] });
            }*/
              }}
            />
          )}
          <Spacer right />
        </DialogActions>
        {isUpdate && !isPartnerView && (
          <DialogActions sx={{ pt: 0 }}>
            <CreatedUpdatedBy
              objectName="Booking"
              formHelperTextProps={{
                //component: "div",
                sx: {},
              }}
              item={item}
              sx={{
                p: 1,
                pl: 0,
                paddingTop: 0,
                paddingBottom: 0.5,
                marginRight: "auto",
                display: "flex",
              }}
              renderDialog={
                hasPermissions(["history.viewany", "history.viewown"])
                  ? props => <HistoryListDialog {...props} subtitle={title} />
                  : undefined
              }
            />
          </DialogActions>
        )}
        <DialogActions sx={{ pt: 0 }}>
          <Button
            size="large"
            title={I18n.t("App.save")}
            disabled={!isDirty}
            onClick={() => save()}
            loading={saving}
            fullWidth
          />
        </DialogActions>

        <BookingDocumentListModal
          type={BOOKING_DOCUMENT_TYPES.SCRIPT}
          open={documentModalOpen}
          onClose={() => setDocumentModalOpen(false)}
        />
        <PaymentManagementDialog
          open={paymentDialogOpen}
          onClose={() => setPaymentDialogOpen(false)}
          bookingId={item?.id}
          bookingEmail={item?.email}
          bookingData={item}
          mode={isPartnerView ? "partner" : "admin"}
        />
      </BookingContext.Provider>
    </ParamsContext.Provider>
  );
}
